import { useEffect, useState } from 'react'
import {
  Jumbotron,
  Container,
  Row,
  Col,
  useAccordionToggle,
  Accordion,
} from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { AccordionSection } from 'components/accordion'
import { applicationRoles } from 'constants/actionTypes'
import { Icon } from 'icons'
import { PrimaryLink } from 'components/styledComponents/link'
import * as E from './homeElements'
import * as C from './contentSectionElements'
import { PrimaryButton } from '@mmsg/louie'
import Tooltip from 'components/tooltip'
import { IHomeContentSectionProps } from './homeTypes'
import { formatCurrency } from 'helpers'
import { remainingLeaseTime } from 'utils/utilityFunctions'
import { ReleaseCard } from '@mmsg/louie'
import { ITheme } from 'utils/type'

const ContentSection = (props: IHomeContentSectionProps) => {
  const [showMoreBenefits, setShowMoreBenefits] = useState(true)
  const CustomToggle = (props: { eventKey: any; theme: ITheme }) => {
    const decoratedOnClick = useAccordionToggle(props.eventKey, () => null)
    return (
      <C.BenefitsToggleText theme={props.theme} onClick={decoratedOnClick}>
        {showMoreBenefits ? 'View more benefits ' : 'Hide benefits '}
        <img
          src={
            showMoreBenefits
              ? '/images/chevron-down.svg'
              : '/images/chevron-up.svg'
          }
        />
      </C.BenefitsToggleText>
    )
  }


  let endDate
  if (props.leaseEndDate && new Date(props.leaseEndDate) > new Date())
    endDate = remainingLeaseTime(props.leaseEndDate)

  let tooltip = (
    <span>
      {' '}
      <Tooltip
        header="Fortnightly out of pocket"
        text="The amount your current lease takes out of your take-home pay."
      />
    </span>
  )
  let releaseIcon = <img src="/images/benefit-car-icon.svg" />

  const IconTextPair = ({ icon, text }: { icon: string; text: string }) => {
    return (
      <Col sm className="text-center">
        <div className="benefit-icon">
          <Icon iconType={icon} primary />
        </div>
        <div className="benefit-option mb-3">{text}</div>
      </Col>
    )
  }

  const NumberedBulletPoint = ({
    itemNumber,
    heading,
    text,
  }: {
    itemNumber: number
    heading: string
    text: string
  }) => {
    return (
      <div>
        <C.NumberedBulletPointHeadingDiv>
          <C.BlackNumberCircle theme={props.theme}>
            {itemNumber}
          </C.BlackNumberCircle>

          <C.StyledH2 className="mb-0 ml-3">
            {heading}
          </C.StyledH2>
        </C.NumberedBulletPointHeadingDiv>
        <C.IndentedP className="text-grey-a">{text}</C.IndentedP>
      </div>
    )
  }

  return (
    <>
      <Jumbotron
        fluid
        className="bg-white section"
        style={{ maxWidth: '996px' }}
      >
        {props.isrelease && props.currentNLOutOfPocket > 0 ? (
          <Container>
            <h2 className="text-uppercase">Your current expiring lease</h2>
            <Row>
              <Col sm={8}>
                <C.TitleDiv theme={props.theme}>
                  Let’s recap the key benefits
                </C.TitleDiv>

                <C.TickListDiv>
                  <img src="/images/select-icon.svg" alt="tick" />
                  You may have lowered your overall taxable income.
                </C.TickListDiv>
                <C.TickListDiv>
                  <img src="/images/select-icon.svg" alt="tick" />
                  You may have paid less tax on your vehicle's running expenses
                  including:
                  <br />
                  - registration
                  <br />
                  - roadside assist
                  <br />
                  - insurance
                  <br />
                  - servicing
                  <br />
                  - fuel
                  <br />
                  - car washing
                  <br />- new tyres
                </C.TickListDiv>
                <C.TickListDiv>
                  <img src="/images/select-icon.svg" alt="tick" />
                  Your automatic payment covered all your vehicle's regular
                  expenses - which means you never had to worry about finding
                  money for fuel, your registration or insurance.
                </C.TickListDiv>
                <C.TickListDiv>
                  <img src="/images/select-icon.svg" alt="tick" />
                  Plus many more!
                </C.TickListDiv>
              </Col>
              <Col
                sm={4}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <ReleaseCard
                  formattedOutOfPocket={formatCurrency(
                    props.currentNLOutOfPocket
                  )}
                  registrationNumber={props.registrationNumber}
                  remainingTime={endDate}
                  tooltip={tooltip}
                  icon={releaseIcon}
                  theme={props.theme}
                />
              </Col>
            </Row>
          </Container>
        ) : (
          //Not a re-lease
          <Container>
            <Row>
              <Col sm={6}>
                <h1 style={{fontSize:'32px', fontWeight:550}}>Quick refresher on novated leasing</h1>
                <p>
                  First, imagine a single payment each week/fortnight/month – whenever you get paid, that puts you behind the wheel of a shiny new car AND covers <i>all</i> your regular car expenses. We’re talking fuel, insurance, rego, servicing… everything!
                </p>
                <p>
                  Now imagine that part of that payment comes out of your salary <i>before</i> tax. Not only could it make it easy to budget, but it could reduce the amount of income tax you pay. Plus, you could get even <i>more</i> savings on eligible electric cars thanks to the government’s EV Discount initiative... Pretty good, right? 
                </p>
                <p>
                  That’s novated leasing, and Oly’s here to make it easy.
                </p>
              </Col>
              <Col sm={1}></Col>
              <Col sm={5} className="text-center">
                <E.CircleImageIcon
                  src="/images/novated-lease-icon.png"
                  alt=""
                />
              </Col>
            </Row>
          </Container>
        )}
      </Jumbotron>
      <Jumbotron fluid className="section-benefits" style={{backgroundColor:'#f2ede8'}}>
        <Container className='text-center'>
          <E.BrandedH1 theme={props.theme} className='text-center'>
            Why should you get a novated lease with Oly?            
          </E.BrandedH1>
          <Row className="ui-list-container">
            <IconTextPair
              icon="IconPiggy"
              text="A novated lease could save you thousands of dollars in tax"
            />
            <IconTextPair icon="LessTax" text="Oly’s got hook-ups with the best car dealerships in the country, so you could get a great deal" />
            <IconTextPair
              icon="ElectricityBig"
              text="Novated leases are the *only* way to take advantage of the government's EV Discount"
            />
            <IconTextPair
              icon="BudgetBetter"
              text="Roll all those car-related expenses into one nifty payment that makes budgeting a breeze"
            />
          </Row>
          <div className="accordion-benefits" id="accordion-benefits">
            <div onClick={() => setShowMoreBenefits(!showMoreBenefits)}>
              <Accordion>
                <Accordion.Collapse eventKey="0" className="expandAccordion">
                  {
                    <div>
                      <Row className="ui-list-container">
                        <IconTextPair
                          icon="FleetDiscount"
                          text="With a novated lease there is no upfront GST cost to you on a new car"
                        />
                        <IconTextPair
                          icon="Paperwork"
                          text="Oly makes it easy by taking care of the paperwork for you"
                        />
                        <IconTextPair
                          icon="IconMobile"
                          text="Stay in control of your lease with Oly's handy app"
                        />
                        <IconTextPair
                          icon="CustomerService"
                          text="Local, everyday support by real Aussies - no lobbing you offshore over some distant, crackling phoneline!"
                        />
                      </Row>
                    </div>
                  }
                </Accordion.Collapse>
                <C.BenefitsToggleParentDiv>
                  <C.BenefitsToggleHRDiv>
                    <hr></hr>
                  </C.BenefitsToggleHRDiv>

                  <C.BenefitsToggleTextDiv
                    theme={props.theme}
                    id="contentSection.benefitstoggletext"
                  >
                    <CustomToggle eventKey="0" theme={props.theme} />
                  </C.BenefitsToggleTextDiv>
                </C.BenefitsToggleParentDiv>
              </Accordion>
            </div>
          </div>
        </Container>
      </Jumbotron>
      <Jumbotron
        fluid
        className="bg-white section section-estimate-steps text-center"
      >
        <Container style={{ textAlign: 'left' }}>
          <C.PinkStyledH2 >
            What do I do next?
          </C.PinkStyledH2>
          <NumberedBulletPoint
            itemNumber={1}
            heading={'Take a look over your estimate.'}
            text={
              "Your estimate isn't a final offer, so if you want to make any changes to any of the info (like the kind of car you want, the lease term, or anything else...), get in touch. Once you're happy with all the details, just hit that 'accept estimate' button."
            }
          />

          <NumberedBulletPoint
            itemNumber={2}
            heading={
              'Let Oly take care of the admin.'
            }
            text={
              'Sit back while Oly handles the financial application process with the financier. Oly will even get in touch with your boss to set up the automated payment from your salary.'
            }
          />

          <NumberedBulletPoint
            itemNumber={3}
            heading={'Give Oly your autograph.'}
            text={
              'Picked your new car and happy with all the numbers? Time to sign on the (digital) dotted line.'
            }
          />

          <NumberedBulletPoint
            itemNumber={4}
            heading={'Get in your new ride.'}
            text={
              "You hear that? That's the open road calling your name. Put on your seatbelt, pick a playlist and go!"
            }
          />

          <div className="text-center">
            {props.applicationRole &&
              props.applicationRole !== applicationRoles.CONSULTANT && (
                <Link to="/estimates">
                  <PrimaryButton extendInMob className="bg-brand">
                    View my estimates
                  </PrimaryButton>
                </Link>
              )}
            {props.applicationRole &&
              props.applicationRole !== applicationRoles.CONSULTANT && (
                <Link to="/consultant/estimates">
                  <PrimaryButton extendInMob className="bg-brand">
                    View my estimates
                  </PrimaryButton>
                </Link>
              )}
          </div>
        </Container>
      </Jumbotron>
    </>
  )
}

export default ContentSection
